import { Link } from 'react-router-dom'
import PageHero from '@/components/PageHero'
import { Reveal, CountUp, ImageFrame } from '@/components/Reveal'
import { HelixStrand, HelixBand } from '@/components/Helix'

const glance = [
  { value: 100, suffix: '+', label: 'Fixed inbred lines in active maintenance' },
  { value: 25, suffix: '', label: 'Consecutive seasons of selection & trial data' },
  { value: 6, suffix: '+', label: 'Heterotic groups across white & yellow pools' },
  { value: 4, suffix: '', label: 'Commercial hybrid families released (HX series)' },
]

const method = [
  {
    phase: 'Season 1–2',
    title: 'Crossing & population creation',
    body: 'Selected parents from complementary heterotic groups are hand-crossed in the nursery. Each cross is chosen for a defined objective — yield stability, stress tolerance, standability, or grain quality — never at random.',
  },
  {
    phase: 'Season 3–8',
    title: 'Selfing & line fixation',
    body: 'Segregating populations are self-pollinated generation by generation (S1 through S6+), with intense visual and measured selection at every stage. Only a small fraction of plants survive each cycle; a line takes roughly six to eight seasons to fix.',
  },
  {
    phase: 'Season 7–9',
    title: 'Testcross evaluation',
    body: 'New fixed lines are crossed to known testers to measure combining ability. Lines that fail to produce superior hybrids are discarded — the library stays lean and purposeful.',
  },
  {
    phase: 'Season 9–12',
    title: 'Multi-location yield trials',
    body: 'Promising combinations enter replicated trials across multiple regions and seasons. Only combinations that outperform commercial checks consistently, across environments, advance toward release.',
  },
]

const pipeline = [
  {
    n: 'Stage 01',
    title: 'Germplasm & line development',
    stat: '~8 seasons',
    body: 'Inbred line creation, selfing, purification and maintenance of the 100+ line library. Full pedigree records kept for every line.',
  },
  {
    n: 'Stage 02',
    title: 'Testcross screening',
    stat: '1–2 seasons',
    body: 'Hybrid combination screening for general and specific combining ability against proven testers.',
  },
  {
    n: 'Stage 03',
    title: 'Advanced yield trials',
    stat: '2–3 seasons',
    body: 'Replicated, multi-location trials across South Africa\'s production regions, benchmarked against leading commercial checks.',
  },
  {
    n: 'Stage 04',
    title: 'Commercial release',
    stat: 'Ongoing',
    body: 'Variety registration, licensing to seed partners, and Hylix-branded production of select hybrids.',
  },
]

const regions = [
  ['Free State', 'Core summer dryland — the heartland of SA maize'],
  ['North West', 'Dryland & marginal-rainfall stress screening'],
  ['Mpumalanga', 'High-potential Highveld conditions'],
  ['KwaZulu-Natal', 'Higher-rainfall, disease-pressure environments'],
  ['Irrigation schemes', 'High-input performance under pivot & flood irrigation'],
]

const timeline = [
  ['~2000', 'Program founded', 'First breeding crosses made; the long work of building inbred lines begins.'],
  ['Mid-2000s', 'First fixed lines', 'Early inbred lines stabilize; recurrent selection cycles established.'],
  ['Late 2000s', 'First commercial partnerships', 'Hylix genetics enter commercial seed portfolios through licensing.'],
  ['2010s', 'HX series released', 'White and yellow hybrid families commercialized with seed company partners.'],
  ['Today', '100+ lines, active pipeline', 'A complete, documented program — germplasm, data, and commercial proof.'],
]

const programIncludes = [
  ['Germplasm library', '100+ fixed inbred lines across multiple heterotic groups, with seed stocks and regeneration protocols.'],
  ['Pedigree & selection records', 'Complete breeding records for every line — parentage, selection history, and observed traits.'],
  ['Trial data archive', '25 seasons of field trial results across regions, seasons, and stress environments.'],
  ['Commercial track record', 'Released hybrids with documented market performance through established seed company partners.'],
  ['Breeder knowledge', 'The founder\'s accumulated knowledge of the material — what each line is, and what it can do.'],
]

export default function BreedingProgram() {
  return (
    <>
      <PageHero
        eyebrow="The Breeding Program"
        title="Built one season at a time"
      >
        100+ inbred lines. Multiple heterotic groups. A complete, documented pipeline from
        germplasm to commercial hybrid — and the field data to prove it.
      </PageHero>

      {/* ————— AT A GLANCE ————— */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
            <div>
              <Reveal className="eyebrow">Program Overview</Reveal>
              <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
                Scale, depth, commercial proof
              </Reveal>
              <Reveal as="p" delay={0.16} className="mt-7 leading-[1.85]" style={{ color: '#565650' }}>
                Hylix operates a full-cycle breeding program: germplasm development, inbred line
                fixation, hybrid combination testing, and commercial release. It has been refined
                continuously across more than two decades of field selection under South African
                conditions — dryland and irrigation, high-potential and marginal.
              </Reveal>
              <Reveal as="p" delay={0.22} className="mt-5 leading-[1.85]" style={{ color: '#565650' }}>
                The result is not a collection of experiments. It is a coherent, documented body of
                genetic work with a functioning commercial output — four hybrid families on the
                market through licensed partners, and a pipeline behind them.
              </Reveal>
            </div>
            <div className="grid grid-cols-2 gap-px self-start overflow-hidden" style={{ backgroundColor: '#D9DED5', border: '1px solid #D9DED5' }}>
              {glance.map((s) => (
                <div key={s.label} className="p-8" style={{ backgroundColor: '#FAFBF9' }}>
                  <div className="font-mono-data text-4xl font-light tracking-[-0.02em]" style={{ color: '#C98E33' }}>
                    <CountUp value={s.value} suffix={s.suffix} />
                  </div>
                  <div className="mt-3 text-[12px] font-medium leading-relaxed" style={{ color: '#565650' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ————— METHOD ————— */}
      <section className="relative overflow-hidden py-24 md:py-32" style={{ backgroundColor: '#FAFBF9' }}>
        <div className="pointer-events-none absolute right-[4%] top-24 hidden xl:block">
          <HelixStrand height={640} width={100} turns={3} stroke="#8BA872" opacity={0.28} />
        </div>
        <div className="container-hx relative">
          <Reveal className="eyebrow">The Method</Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 max-w-2xl text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
            Conventional breeding, done properly
          </Reveal>
          <Reveal as="p" delay={0.16} className="mt-6 max-w-2xl leading-[1.85]" style={{ color: '#565650' }}>
            No shortcuts, no genetic modification. Every Hylix hybrid descends from disciplined
            pedigree selection: hand pollination, generation-by-generation selfing, and ruthless
            culling. It is slow by design — and that slowness is exactly what the data captures.
          </Reveal>

          <div className="mt-16 space-y-0 border-t" style={{ borderColor: '#D9DED5' }}>
            {method.map((m) => (
              <div
                key={m.title}
                className="grid gap-4 border-b py-10 transition-colors duration-500 hover:bg-[#F3F5F1] md:grid-cols-[200px_240px_1fr] md:gap-10 md:px-6"
                style={{ borderColor: '#D9DED5' }}
              >
                <div className="font-mono-data text-[12px] uppercase tracking-[0.16em]" style={{ color: '#C98E33' }}>
                  {m.phase}
                </div>
                <h3 className="text-lg font-medium leading-snug" style={{ color: '#24391C' }}>
                  {m.title}
                </h3>
                <p className="text-[15px] leading-[1.8]" style={{ color: '#565650' }}>
                  {m.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————— GERMPLASM LIBRARY ————— */}
      <section className="relative overflow-hidden py-24 md:py-32" style={{ backgroundColor: '#24391C' }}>
        <div className="container-hx grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal className="eyebrow" style={{ color: '#8BA872' }}>
              The Core Asset
            </Reveal>
            <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance] text-[#F3F5F1]">
              The germplasm library
            </Reveal>
            <Reveal as="p" delay={0.16} className="mt-7 leading-[1.85] text-[#F3F5F1]/70">
              The library spans multiple heterotic groups across white and yellow pools, with
              deliberately maintained breadth — lines chosen not only for what they deliver today,
              but for the options they preserve: new disease pressures, shifting rainfall patterns,
              new regional demands.
            </Reveal>
            <Reveal as="p" delay={0.22} className="mt-5 leading-[1.85] text-[#F3F5F1]/70">
              Every line carries a full pedigree and selection history. Seed is regenerated on a
              managed cycle to protect viability and genetic purity. This diversity — documented,
              alive, and tested in combination — is the foundation every Hylix hybrid is built on.
            </Reveal>
            <Reveal delay={0.28} className="mt-10 grid grid-cols-2 gap-px overflow-hidden" style={{ backgroundColor: 'rgba(243,245,241,0.15)', border: '1px solid rgba(243,245,241,0.15)' }}>
              {[
                ['White pool', 'Food-grade white maize lines'],
                ['Yellow pool', 'Feed & industrial yellow lines'],
                ['Heterotic coverage', '6+ complementary groups'],
                ['Documentation', 'Full pedigrees, every line'],
              ].map(([a, b]) => (
                <div key={a} className="p-6" style={{ backgroundColor: '#24391C' }}>
                  <div className="font-mono-data text-[11px] uppercase tracking-[0.16em]" style={{ color: '#E8B85C' }}>
                    {a}
                  </div>
                  <div className="mt-2 text-sm font-light text-[#F3F5F1]/75">{b}</div>
                </div>
              ))}
            </Reveal>
          </div>
          <ImageFrame
            src="/images/seed-archive.jpg"
            alt="The Hylix seed archive — catalogued germplasm samples"
            ratio="4/5"
            caption="The seed archive — catalogued & regenerated"
          />
        </div>
      </section>

      {/* ————— PIPELINE ————— */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx">
          <Reveal className="eyebrow">From Cross to Release</Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 max-w-2xl text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
            From germplasm to commercial hybrid
          </Reveal>
          <Reveal delay={0.14} className="mt-10">
            <HelixBand height={44} turns={12} />
          </Reveal>
          <div className="mt-10 grid gap-px overflow-hidden md:grid-cols-2 xl:grid-cols-4" style={{ backgroundColor: '#D9DED5', border: '1px solid #D9DED5' }}>
            {pipeline.map((p) => (
              <div key={p.n} className="group flex flex-col p-9 transition-colors duration-500 hover:bg-white" style={{ backgroundColor: '#FAFBF9' }}>
                <div className="flex items-baseline justify-between">
                  <span className="font-mono-data text-[11px] uppercase tracking-[0.18em]" style={{ color: '#96968A' }}>
                    {p.n}
                  </span>
                  <span className="font-mono-data text-[11px]" style={{ color: '#C98E33' }}>
                    {p.stat}
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-medium leading-snug" style={{ color: '#24391C' }}>
                  {p.title}
                </h3>
                <p className="mt-4 text-[14px] leading-[1.75]" style={{ color: '#565650' }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————— FIELD TESTING ————— */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#FAFBF9' }}>
        <div className="container-hx grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <div>
            <Reveal className="eyebrow">Field Testing</Reveal>
            <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
              Tested where it has to grow
            </Reveal>
            <Reveal as="p" delay={0.16} className="mt-7 leading-[1.85]" style={{ color: '#565650' }}>
              Hylix hybrids are evaluated across South Africa's major maize production regions, in
              replicated trials over multiple seasons. Performance is measured against leading
              commercial checks — a hybrid only advances if it wins across environments, not in one
              good year.
            </Reveal>
            <Reveal as="p" delay={0.22} className="mt-5 leading-[1.85]" style={{ color: '#565650' }}>
              Twenty-five seasons of these results form the trial data archive: yield, standability,
              disease reaction, and stability records that give seed companies a ready-made evidence
              base for registration and marketing.
            </Reveal>
          </div>
          <div className="self-start border-t" style={{ borderColor: '#D9DED5' }}>
            {regions.map(([r, d]) => (
              <div
                key={r}
                className="flex items-baseline justify-between gap-8 border-b py-6 transition-colors duration-300 hover:bg-[#F3F5F1] md:px-4"
                style={{ borderColor: '#D9DED5' }}
              >
                <span className="text-[15px] font-medium" style={{ color: '#24391C' }}>
                  {r}
                </span>
                <span className="text-right text-[13px] font-light" style={{ color: '#96968A' }}>
                  {d}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ————— TIMELINE ————— */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#E9EDE7' }}>
        <div className="container-hx">
          <Reveal className="eyebrow">25 Seasons</Reveal>
          <Reveal as="h2" delay={0.08} className="mt-5 max-w-2xl text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
            Milestones
          </Reveal>
          <div className="relative mt-16">
            <div className="absolute bottom-0 left-[7px] top-0 w-px md:left-1/2" style={{ backgroundColor: '#D9DED5' }} />
            <div className="space-y-14">
              {timeline.map(([year, title, body], i) => (
                <div key={title} className="relative grid gap-3 pl-10 md:grid-cols-2 md:gap-16 md:pl-0">
                  <div className="absolute left-0 top-2 h-[15px] w-[15px] rotate-45 border md:left-1/2 md:-translate-x-1/2" style={{ borderColor: '#C98E33', backgroundColor: '#E9EDE7' }} />
                  <div className={`${i % 2 ? 'md:order-2 md:pl-16' : 'md:pr-16 md:text-right'}`}>
                    <div className="font-mono-data text-[12px] uppercase tracking-[0.18em]" style={{ color: '#C98E33' }}>
                      {year}
                    </div>
                    <h3 className="mt-2 text-lg font-medium" style={{ color: '#24391C' }}>
                      {title}
                    </h3>
                  </div>
                  <p className={`text-[15px] leading-[1.8] ${i % 2 ? 'md:order-1 md:pr-16 md:text-right' : 'md:pl-16'}`} style={{ color: '#565650' }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ————— THE COMPLETE PACKAGE ————— */}
      <section className="py-24 md:py-32" style={{ backgroundColor: '#F3F5F1' }}>
        <div className="container-hx">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
            <div>
              <Reveal className="eyebrow">The Complete Package</Reveal>
              <Reveal as="h2" delay={0.08} className="mt-5 text-[clamp(1.8rem,3.2vw,2.5rem)] font-light leading-[1.2] tracking-[-0.01em] [text-wrap:balance]">
                What the program includes
              </Reveal>
              <Reveal as="p" delay={0.16} className="mt-7 leading-[1.85]" style={{ color: '#565650' }}>
                The Hylix program is a rare, complete package in African agriculture: a fully
                developed, independent germplasm library with commercial hybrids already on the
                market — available for licensing and long-term partnership. Every element is
                documented and ready to work from the first season.
              </Reveal>
              <Reveal delay={0.24} className="mt-10">
                <Link to="/#contact" className="btn-primary">
                  Discuss Licensing
                </Link>
              </Reveal>
            </div>
            <div className="space-y-0 self-start border-t" style={{ borderColor: '#D9DED5' }}>
              {programIncludes.map(([t, d], i) => (
                <div key={t} className="border-b py-7" style={{ borderColor: '#D9DED5' }}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono-data text-[11px]" style={{ color: '#C98E33' }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-[15px] font-medium" style={{ color: '#24391C' }}>
                      {t}
                    </h3>
                  </div>
                  <p className="mt-2 pl-9 text-[14px] leading-[1.75]" style={{ color: '#565650' }}>
                    {d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
